import { useEffect, useRef, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { format } from "date-fns";
import { Maximize2, Minimize2, Play, Pause, SkipBack, SkipForward } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { type Position } from "@/lib/traccar-api";
import { MapStylePicker } from "@/components/map/MapStylePicker";
import { useMapInit } from "@/hooks/useMapInit";

interface RouteReplayMapProps {
  positions: Position[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  className?: string;
  focusTime?: number | null;
  // Playback controls — rendered as overlay when fullscreen
  playing?: boolean;
  onPlayPause?: () => void;
  onStepForward?: () => void;
  onStepBackward?: () => void;
  playbackSpeed?: number;
  onSpeedChange?: (speed: number) => void;
}

export const RouteReplayMap = ({ positions, currentIndex, onIndexChange, className, focusTime, playing, onPlayPause, onStepForward, onStepBackward, playbackSpeed = 1, onSpeedChange }: RouteReplayMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const layersReady = useRef(false);
  const positionsRef = useRef<Position[]>(positions);
  positionsRef.current = positions;
  const focusTimeRef = useRef<number | null | undefined>(focusTime);
  focusTimeRef.current = focusTime;

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const { map, currentStyle, handleStyleChange } = useMapInit(mapContainer, {
    onBeforeStyleChange: () => { layersReady.current = false; },
  });

  const addLayers = useCallback(() => {
    const m = map.current;
    if (!m || layersReady.current) return;

    // Create arrow image for direction indicators
    if (!m.hasImage('route-arrow')) {
      const size = 16;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(size / 2, 1);
      ctx.lineTo(size - 2, size - 2);
      ctx.lineTo(size / 2, size * 0.6);
      ctx.lineTo(2, size - 2);
      ctx.closePath();
      ctx.fill();
      const imageData = ctx.getImageData(0, 0, size, size);
      m.addImage('route-arrow', { width: size, height: size, data: new Uint8Array(imageData.data.buffer) }, { sdf: true });
    }

    if (!m.getSource('route')) {
      m.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
        },
      });
    }

    if (!m.getLayer('route-casing')) {
      m.addLayer({
        id: 'route-casing',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': 'black',
          'line-width': 14,
        }
      });
    }

    if (!m.getLayer('route-line')) {
      m.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': 'white',
          'line-width': 10,
        }
      });
    }

    // Direction arrows source (route points)
    if (!m.getSource('route-points')) {
      m.addSource('route-points', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
    }

    if (!m.getLayer('route-arrows')) {
      m.addLayer({
        id: 'route-arrows',
        type: 'symbol',
        source: 'route-points',
        layout: {
          'icon-image': 'route-arrow',
          'icon-size': 1.2,
          'icon-overlap': 'cooperative',
          'icon-rotate': ['get', 'course'],
          'icon-rotation-alignment': 'map',
        },
        paint: {
          'icon-color': [
            'interpolate', ['linear'], ['get', 'speed'],
            0, '#22c55e',
            30, '#84cc16',
            60, '#eab308',
            90, '#f97316',
            120, '#ef4444'
          ],
          'icon-halo-color': 'rgba(0,0,0,0.5)',
          'icon-halo-width': 1,
        }
      });
    }

    layersReady.current = true;

    if (positionsRef.current.length > 0) {
      updateRouteOnMap(positionsRef.current);
    }
  }, []);

  const updateRouteOnMap = useCallback((route: Position[]) => {
    const m = map.current;
    if (!m || !layersReady.current) return;

    const routeSource = m.getSource('route') as maplibregl.GeoJSONSource;
    const pointsSource = m.getSource('route-points') as maplibregl.GeoJSONSource;
    if (!routeSource) return;

    if (route.length === 0) {
      routeSource.setData({ type: 'FeatureCollection', features: [] });
      if (pointsSource) pointsSource.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    routeSource.setData({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: route.map(p => [p.longitude, p.latitude])
      }
    });

    // Direction point markers (every nth point for performance)
    if (pointsSource) {
      const step = Math.max(1, Math.floor(route.length / 200));
      const pointFeatures: GeoJSON.Feature[] = [];
      for (let i = 0; i < route.length; i += step) {
        const p = route[i];
        pointFeatures.push({
          type: 'Feature',
          properties: { index: i, course: p.course || 0, speed: (p.speed || 0) * 1.852 },
          geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] }
        });
      }
      pointsSource.setData({ type: 'FeatureCollection', features: pointFeatures });
    }

    // Fit bounds or fly to focus position
    const coordinates = route.map(p => [p.longitude, p.latitude]);
    if (coordinates.length > 0) {
      if (focusTimeRef.current != null) {
        let closest = 0;
        let minDiff = Infinity;
        route.forEach((p, i) => {
          const diff = Math.abs(new Date(p.fixTime).getTime() - focusTimeRef.current!);
          if (diff < minDiff) { minDiff = diff; closest = i; }
        });
        m.flyTo({ center: [route[closest].longitude, route[closest].latitude], zoom: 15 });
      } else {
        const bounds = coordinates.reduce(
          (b, coord) => b.extend(coord as [number, number]),
          new maplibregl.LngLatBounds(coordinates[0] as [number, number], coordinates[0] as [number, number])
        );
        m.fitBounds(bounds, { padding: 50, maxZoom: 15 });
      }
    }
  }, []);

  useEffect(() => {
    if (!map.current) return;

    map.current.on('styledata', () => addLayers());

    map.current.on('click', 'route-arrows', (e) => {
      if (e.features?.[0]) {
        const idx = e.features[0].properties?.index;
        if (typeof idx === 'number') onIndexChange(idx);
      }
    });

    map.current.on('click', 'route-line', (e) => {
      if (!e.lngLat || positionsRef.current.length === 0) return;
      const { lng, lat } = e.lngLat;
      let minDist = Infinity, nearestIdx = 0;
      positionsRef.current.forEach((p, i) => {
        const d = (p.longitude - lng) ** 2 + (p.latitude - lat) ** 2;
        if (d < minDist) { minDist = d; nearestIdx = i; }
      });
      onIndexChange(nearestIdx);
    });

    map.current.on('mouseenter', 'route-arrows', () => { if (map.current) map.current.getCanvas().style.cursor = 'pointer'; });
    map.current.on('mouseleave', 'route-arrows', () => { if (map.current) map.current.getCanvas().style.cursor = ''; });
    map.current.on('mouseenter', 'route-line', () => { if (map.current) map.current.getCanvas().style.cursor = 'pointer'; });
    map.current.on('mouseleave', 'route-line', () => { if (map.current) map.current.getCanvas().style.cursor = ''; });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update route when positions change
  useEffect(() => {
    if (!map.current || !layersReady.current) return;
    updateRouteOnMap(positions);

    // Remove old marker
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
  }, [positions, updateRouteOnMap]);

  // Update device marker position when currentIndex changes
  useEffect(() => {
    if (!map.current || positions.length === 0) return;

    const pos = positions[currentIndex];
    if (!pos) return;

    if (!markerRef.current) {
      const el = document.createElement('div');
      el.className = 'replay-marker';
      el.innerHTML = `
        <div style="position:relative; width:28px; height:28px;">
          <div style="
            width: 20px; height: 20px;
            position: absolute; top: 4px; left: 4px;
            background: #f43f5e;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 0 0 3px rgba(244,63,94,0.4), 0 2px 6px rgba(0,0,0,0.3);
          "></div>
          <div class="replay-arrow" style="
            position: absolute; top: -10px; left: 50%; transform: translateX(-50%);
            width: 0; height: 0;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-bottom: 10px solid #f43f5e;
            filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
          "></div>
        </div>
      `;
      markerRef.current = new maplibregl.Marker({ element: el, anchor: 'center', rotationAlignment: 'map' })
        .setRotation(pos.course || 0)
        .setLngLat([pos.longitude, pos.latitude])
        .addTo(map.current);
    } else {
      markerRef.current.setLngLat([pos.longitude, pos.latitude]);
      markerRef.current.setRotation(pos.course || 0);
    }
  }, [currentIndex, positions]);

  return (
    <div ref={(el) => { containerRef.current = el; setContainerEl(el); }} className={`relative rounded-lg overflow-hidden ${className}`}>
      <div ref={mapContainer} className="w-full h-full" />

      {/* Layer Switcher + Fullscreen */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
        <MapStylePicker currentStyle={currentStyle} onStyleChange={handleStyleChange} />
        <Button variant="secondary" size="sm" className="shadow-md" onClick={toggleFullscreen}>
          {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </Button>
      </div>

      {/* Speed Legend — hidden in fullscreen (shown inside the overlay instead) */}
      {positions.length > 0 && !isFullscreen && (
        <div className="absolute bottom-4 left-3 z-10 bg-background/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border">
          <p className="text-xs font-medium text-muted-foreground mb-2">Speed (km/h)</p>
          <div className="flex items-center gap-1">
            <div className="w-8 h-2 rounded-sm" style={{ background: '#22c55e' }} />
            <div className="w-8 h-2 rounded-sm" style={{ background: '#84cc16' }} />
            <div className="w-8 h-2 rounded-sm" style={{ background: '#eab308' }} />
            <div className="w-8 h-2 rounded-sm" style={{ background: '#f97316' }} />
            <div className="w-8 h-2 rounded-sm" style={{ background: '#ef4444' }} />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>0</span>
            <span>30</span>
            <span>60</span>
            <span>90</span>
            <span>120+</span>
          </div>
        </div>
      )}

      {/* No positions message */}
      {positions.length === 0 && (
        <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-20">
          <p className="text-muted-foreground">Select a device and date range to view route</p>
        </div>
      )}

      {/* Fullscreen playback overlay */}
      {isFullscreen && onPlayPause && positions.length > 0 && (() => {
        const currentPos = positions[currentIndex];
        const speeds = positions.map(p => (p.speed || 0) * 1.852);
        const maxSpeed = Math.max(...speeds, 1);
        const W = 640, H = 48;
        const speedColor = (kmh: number) => {
          if (kmh < 30) return '#22c55e';
          if (kmh < 60) return '#84cc16';
          if (kmh < 90) return '#eab308';
          if (kmh < 120) return '#f97316';
          return '#ef4444';
        };

        // Build SVG path segments coloured by speed
        const segmentRects = positions.map((p, i) => {
          const x = (i / (positions.length - 1)) * W;
          const nextX = ((i + 1) / (positions.length - 1)) * W;
          const kmh = (p.speed || 0) * 1.852;
          const h = Math.max(2, (kmh / maxSpeed) * H);
          return { x, w: Math.max(1, nextX - x), h, color: speedColor(kmh) };
        });

        const handleGraphClick = (e: React.MouseEvent<SVGSVGElement>) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const ratio = (e.clientX - rect.left) / rect.width;
          onIndexChange(Math.round(ratio * (positions.length - 1)));
        };

        const cursorX = (currentIndex / Math.max(1, positions.length - 1)) * W;

        return (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 w-[min(680px,90%)] bg-background/90 backdrop-blur-sm rounded-xl p-4 shadow-xl border space-y-3">
            {/* Position details */}
            {currentPos && (
              <div className="grid grid-cols-3 text-xs gap-4">
                <span className="text-muted-foreground">{format(new Date(currentPos.fixTime), 'MMM d, yyyy HH:mm:ss')}</span>
                <span className="font-medium text-center">{Math.round((currentPos.speed || 0) * 1.852)} km/h</span>
                <span className="text-muted-foreground truncate text-right">
                  {currentPos.address || `${currentPos.latitude.toFixed(5)}, ${currentPos.longitude.toFixed(5)}`}
                </span>
              </div>
            )}
            {/* Speed graph + vertical legend */}
            <div className="flex gap-2 items-stretch">
              <svg
                viewBox={`0 0 ${W} ${H}`}
                className="flex-1 cursor-pointer rounded overflow-hidden"
                style={{ height: H }}
                onClick={handleGraphClick}
              >
                <rect width={W} height={H} fill="rgba(0,0,0,0.08)" rx={4} />
                {segmentRects.map((s, i) => (
                  <rect key={i} x={s.x} y={H - s.h} width={s.w + 0.5} height={s.h} fill={s.color} opacity={0.85} />
                ))}
                {/* Cursor line */}
                <line x1={cursorX} y1={0} x2={cursorX} y2={H} stroke="white" strokeWidth={2} />
                <circle cx={cursorX} cy={H - Math.max(2, ((positions[currentIndex]?.speed || 0) * 1.852 / maxSpeed) * H)} r={4} fill="white" stroke="rgba(0,0,0,0.3)" strokeWidth={1} />
              </svg>
              {/* Vertical speed legend */}
              <div className="flex gap-1 items-stretch">
                <div className="flex flex-col flex-1 w-3 gap-px rounded overflow-hidden">
                  <div className="flex-1" style={{ background: '#ef4444' }} />
                  <div className="flex-1" style={{ background: '#f97316' }} />
                  <div className="flex-1" style={{ background: '#eab308' }} />
                  <div className="flex-1" style={{ background: '#84cc16' }} />
                  <div className="flex-1" style={{ background: '#22c55e' }} />
                </div>
                <div className="flex flex-col justify-between text-[9px] text-muted-foreground leading-none">
                  <span>120+</span>
                  <span>90</span>
                  <span>60</span>
                  <span>30</span>
                  <span>0</span>
                </div>
              </div>
            </div>
            <Slider
              value={[currentIndex]}
              min={0}
              max={Math.max(0, positions.length - 1)}
              step={1}
              onValueChange={([val]) => onIndexChange(val)}
            />
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" size="icon" onClick={onStepBackward} disabled={currentIndex === 0}>
                <SkipBack className="h-4 w-4" />
              </Button>
              <Button variant="default" size="icon" className="h-10 w-10" onClick={onPlayPause}>
                {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              </Button>
              <Button variant="outline" size="icon" onClick={onStepForward} disabled={currentIndex >= positions.length - 1}>
                <SkipForward className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums">{currentIndex + 1} / {positions.length}</span>
              <div className="flex items-center gap-1 ml-2">
                {[1, 2, 5, 10].map(speed => (
                  <Button key={speed} size="sm" variant={playbackSpeed === speed ? "default" : "outline"} className="h-7 w-9 text-xs" onClick={() => onSpeedChange?.(speed)}>
                    {speed}×
                  </Button>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default RouteReplayMap;
